import { useRef, useState, useEffect } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

interface UseIntersectionObserverOptions {
  /** CSS rootMargin, e.g. '-10% 0px'. Default: '-10% 0px' */
  rootMargin?: string;
  /** Intersection threshold. Default: 0.1 */
  threshold?: number;
  /** If true, observer re-fires on each intersection (default false = fire once). */
  repeat?: boolean;
}

/**
 * Single hook that replaces all 12+ manual IntersectionObservers from app.js.
 * Returns [ref, isVisible]. Attach `ref` to the element; use `isVisible`
 * to toggle animation CSS classes.
 *
 * When prefers-reduced-motion is active, returns isVisible=true immediately.
 */
export function useIntersectionObserver<T extends HTMLElement = HTMLElement>(
  options: UseIntersectionObserverOptions = {},
): [React.RefObject<T | null>, boolean] {
  const { rootMargin = '-10% 0px', threshold = 0.1, repeat = false } = options;
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const prefersReduced = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReduced) {
      setIsVisible(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (!repeat) {
              observer.unobserve(entry.target);
            }
          } else if (repeat) {
            setIsVisible(false);
          }
        }
      },
      { rootMargin, threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, threshold, repeat, prefersReduced]);

  return [ref, isVisible];
}
